import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { QueryKey } from '@/Config/api/queryKey';
import { ConversationBadgeDto } from '@/Domain/api/Conversation/dto/ConversationBadge.dto';

export type UnifiedInsightItem = {
  conversation_conv_id: string;
  created_timestamp: string;
  conversation_timestamp: string | null;
  is_flagged: boolean;
  flag_reason: string | null;
  market_insight: string | null;
  open_ended_topic: string | null;
  market_insight_reviewed_at: string | null;
  // Short excerpt from the conversation used to disambiguate rows that share
  // an alert name (e.g. "Refund or chargeback request" matched 10 times today).
  conversation_preview: string | null;
  // Populated when the row represents a single matched alert (one
  // ConversationMatchedAlert per row, mirrors backend expansion).
  // alert_match_id is that row's PK, used to toggle its reviewed state.
  alert_match_id: number | null;
  alert_name: string | null;
  alert_reviewed_at: string | null;
  badge?: ConversationBadgeDto | null;
};

type UnifiedInsightsPage = {
  items: UnifiedInsightItem[];
  has_more: boolean;
};

export type InsightType = 'flagged' | 'market_insights' | 'alerts' | 'all';

export const useUnifiedInsights = (insightType: InsightType, topic?: string | null) => {
  const query = useInfiniteQuery<UnifiedInsightsPage>({
    queryKey: [QueryKey.analytics.unifiedInsightsList, { insightType, topic: topic ?? null }],
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.items.length === 0) return undefined;
      return lastPage.items[lastPage.items.length - 1].conversation_timestamp ?? undefined;
    },
    initialPageParam: undefined,
  });

  const onLoadMore = useCallback(() => {
    if (query.isFetchingNextPage) return;
    void query.fetchNextPage();
  }, [query.isFetchingNextPage]);

  const [sentryRef] = useInfiniteScroll({
    loading: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    onLoadMore,
  });

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return { ...query, items, sentryRef };
};
